import { CMMS_TECHNICIAN_ROLES } from "../cmms/cmmsRoles";

/**
 * Whether a user from /api/users is in the Cmms_Technician tier (Technician + Admin) — who the
 * server accepts as a new trade member. TradeService answers anyone else with
 * CMMS_NOT_A_TECHNICIAN.
 *
 * The same rule as the work order assignee pickers (AB#1619), written out here rather than
 * imported: that helper, cmms/cmmsAssigneeOptions, went to master as a hotfix and is not on
 * develop, which this was built on. What the two share is CMMS_TECHNICIAN_ROLES, and that is
 * the rule; the predicate is one line over it.
 */
export const isCmmsTechnician = (user) =>
  (user?.roles ?? []).some((roleId) => CMMS_TECHNICIAN_ROLES.includes(roleId));

const titleOf = (id, name, username) => name || username || `#${id}`;

/**
 * Options for the trade members picker, sorted by title.
 *
 * selectedIds is the picker's live value. Anyone in it stays in the list even when they fall
 * outside the tier — a member who has lost the role since joining has to be on screen to be
 * taken out, and the server keeps them on a save that leaves them in. Pass the live value, not
 * the saved membership: once a stale member is removed they drop out of the list and cannot be
 * picked again, which is the server's rule as well.
 *
 * members is the trade's saved membership, and only a fallback for a selected member that
 * /api/users did not return — it has not loaded yet, or it failed. Without it their chip would
 * disappear from the picker, and the next change to the selection would remove them unseen.
 *
 * Each option carries isTechnician so the dialog can say which members are stale.
 */
export function toTradeMemberOptions(users, selectedIds, members) {
  const selected = new Set(selectedIds ?? []);
  const options = [];
  const seen = new Set();

  for (const user of users ?? []) {
    const isTechnician = isCmmsTechnician(user);
    if (!isTechnician && !selected.has(user.id)) continue;
    seen.add(user.id);
    options.push({ id: user.id, title: titleOf(user.id, user.name, user.username), isTechnician });
  }

  for (const member of members ?? []) {
    if (seen.has(member.userId) || !selected.has(member.userId)) continue;
    seen.add(member.userId);
    options.push({
      id: member.userId,
      title: titleOf(member.userId, member.name, member.username),
      isTechnician: Boolean(member.isTechnician),
    });
  }

  return options.sort((a, b) => a.title.localeCompare(b.title, undefined, { sensitivity: "base" }));
}
